import { Alert, Button } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { OrganizationSelectionModal } from "~/components/Organization/organizationSelectionModal/OrganizationSelectionModal";
import { azureLoginUri } from "~/config/apiConfig";
import { TOAST_MESSAGES } from "~/constants/messages";
import type { SigninResponse } from "~/functions/api/auth";
import { OrganizationApi } from "~/functions/api/organization";
import { UserApi } from "~/functions/api/user";
import type { Organization } from "~/interface/organization";
import type { AuthenticatedUserInfo, UserData } from "~/interface/userInfo";
import {
  clearAuth,
  clearIntendedUrl,
  fetchUserInfo,
  setCurrentUser,
  setCurrentUserInfo,
  setOrganization,
  setSignupAllowed,
} from "~/slices/auth";
import type { AppDispatch } from "~/store";
import styles from "./index.module.scss";

const INTENDED_URL_KEY = "intendedUrl";

const ERROR_CODE_TO_MESSAGE: Record<string, string> = {
  invalid_code: TOAST_MESSAGES.AUTH.CALLBACK_INVALID_CODE,
  session_expired: TOAST_MESSAGES.AUTH.CALLBACK_SESSION_EXPIRED,
  auth_failed: TOAST_MESSAGES.AUTH.CALLBACK_AUTH_FAILED,
  config_error: TOAST_MESSAGES.AUTH.CALLBACK_CONFIG_ERROR,
  server_error: TOAST_MESSAGES.AUTH.CALLBACK_SERVER_ERROR,
};

/**
 * URLからorganization_idを抽出する
 * 例: /org-123/project/456 → org-123
 */
const extractOrgIdFromUrl = (url: string): string | null => {
  const match = url.match(/^\/([^/]+)/);
  return match?.[1] ?? null;
};

/**
 * org_idがユーザーの所属組織に含まれるかチェック
 */
const isValidOrgId = (orgId: string, userData: UserData[]): boolean => {
  return userData.some((data) => data.organization_id === orgId);
};

/**
 * sessionStorageからintendedUrlを取得する（Redux経由で管理）
 */
const getIntendedUrlFromStorage = (): string | null => {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(INTENDED_URL_KEY);
};

const AuthErrorView: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className={styles["errorViewContainer"]}>
    <Alert severity="error" role="alert" className={styles["errorViewAlert"] ?? ""}>
      {message}
    </Alert>
    <Button
      variant="contained"
      onClick={onRetry}
      autoFocus
      className={styles["errorViewRetryButton"] ?? ""}
    >
      ログインページへ
    </Button>
  </div>
);

const Signin: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showOrgModal, setShowOrgModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorView, setShowErrorView] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // 無限ループ防止用のref
  const hasAttemptedAuth = useRef(false);

  const handleGetCurrentOrganization = useCallback(
    async (organization_id: string): Promise<boolean> => {
      const organizationApi = new OrganizationApi();
      const response = await organizationApi.get(organization_id);
      if (response) {
        const organization = response;
        dispatch(setOrganization(organization));
        return true;
      } else {
        toast.error(TOAST_MESSAGES.AUTH.FETCH_ORGANIZATION_FAILED);
        return false;
      }
    },
    [dispatch],
  );

  const handleGetCurrentUser = useCallback(
    async (organization_id: string): Promise<boolean> => {
      const userApi = new UserApi();
      const response = await userApi.get(organization_id);
      if (response) {
        dispatch(setCurrentUser(response));
        return true;
      } else {
        toast.error(TOAST_MESSAGES.AUTH.FETCH_USER_FAILED);
        return false;
      }
    },
    [dispatch],
  );

  const fetchOrganizationsData = useCallback(
    async (userData: UserData[]) => {
      try {
        const organizationApi = new OrganizationApi();
        const orgs: Organization[] = [];

        for (const data of userData) {
          try {
            const org = await organizationApi.get(data.organization_id);
            if (org) {
              orgs.push(org);
            } else {
              toast.error(TOAST_MESSAGES.AUTH.NO_JOINED_ORGANIZATION);
              dispatch(clearAuth());
              window.location.href = azureLoginUri;
              return [];
            }
          } catch (_err) {
            toast.error(TOAST_MESSAGES.AUTH.FETCH_ORGANIZATION_FAILED);
            dispatch(clearAuth());
            window.location.href = azureLoginUri;
            return [];
          }
        }

        return orgs;
      } catch (error) {
        console.error("組織情報取得中にエラーが発生しました:", error);
        return [];
      }
    },
    [dispatch],
  );

  const handleRetryLogin = useCallback(() => {
    dispatch(clearAuth());
    window.location.href = azureLoginUri;
  }, [dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const errorCode = urlParams.get("error");

    if (errorCode) {
      hasAttemptedAuth.current = true;
      const message = ERROR_CODE_TO_MESSAGE[errorCode] ?? TOAST_MESSAGES.AUTH.CALLBACK_SERVER_ERROR;
      setErrorMessage(message);
      setShowErrorView(true);
      setIsLoading(false);
      return;
    }

    // 既に認証を試行済みの場合はスキップ
    if (hasAttemptedAuth.current) {
      return;
    }

    // intendedUrlが有効かチェックし、有効な場合は遷移先として使用
    const getValidIntendedUrl = (userData: UserData[]): string | null => {
      const intendedUrl = getIntendedUrlFromStorage();
      if (!intendedUrl) return null;

      const orgId = extractOrgIdFromUrl(intendedUrl);
      if (!orgId) {
        // 不正なURLの場合はクリア
        dispatch(clearIntendedUrl());
        return null;
      }

      // 所属組織のURLかチェック
      if (isValidOrgId(orgId, userData)) {
        // 使用後にクリア
        dispatch(clearIntendedUrl());
        return intendedUrl;
      }

      // 所属組織ではない場合はクリア
      dispatch(clearIntendedUrl());
      return null;
    };

    // 認証済みユーザーの処理
    const handleAuthenticatedUser = async (userInfo: AuthenticatedUserInfo) => {
      dispatch(setCurrentUserInfo(userInfo));

      // user_dataが空の場合は不正なデータ（招待URLでも同様）
      if (userInfo.user_data.length === 0) {
        toast.error(TOAST_MESSAGES.AUTH.INVALID_USER_DATA);
        dispatch(clearAuth());
        window.location.href = azureLoginUri;
        return;
      }

      // 招待URLの場合は招待サインアップ画面へ
      const inviteUrlParams = new URLSearchParams(location.search);
      const isInvitation = inviteUrlParams.get("type") === "invitation";

      if (isInvitation) {
        const userData = userInfo.user_data[0];
        if (userData) {
          const orgResult = await handleGetCurrentOrganization(userData.organization_id);
          const userResult = await handleGetCurrentUser(userData.organization_id);
          if (!orgResult || !userResult) {
            dispatch(clearAuth());
            window.location.href = azureLoginUri;
            return;
          }
        }
        navigate("/signup-invitation");
        return;
      }

      // intendedUrlが有効な所属組織のURLかチェック
      const validIntendedUrl = getValidIntendedUrl(userInfo.user_data);

      if (validIntendedUrl) {
        // 有効なintendedUrlがある場合は、その組織の情報を取得して直接遷移
        const intendedOrgId = extractOrgIdFromUrl(validIntendedUrl)!;

        const orgResult = await handleGetCurrentOrganization(intendedOrgId);
        const userResult = await handleGetCurrentUser(intendedOrgId);

        if (!orgResult || !userResult) {
          dispatch(clearAuth());
          window.location.href = azureLoginUri;
          return;
        }

        navigate(validIntendedUrl);
        return;
      }

      // intendedUrlがない、または無効な場合は通常フロー
      if (userInfo.user_data.length > 1) {
        // 複数組織に所属している場合は組織選択モーダルを表示
        const orgsList = await fetchOrganizationsData(userInfo.user_data);
        if (orgsList.length > 0) {
          setOrganizations(orgsList);
          setShowOrgModal(true);
        } else {
          toast.error(TOAST_MESSAGES.AUTH.FETCH_ORGANIZATION_FAILED);
          dispatch(clearAuth());
          window.location.href = azureLoginUri;
        }
        return;
      }

      // 単一組織に所属している認証済みユーザー
      const userData = userInfo.user_data[0];
      if (!userData) {
        // user_data配列に要素があるはずなのにundefinedの場合はデータ不整合
        toast.error(TOAST_MESSAGES.AUTH.FETCH_USER_DATA_FAILED);
        dispatch(clearAuth());
        window.location.href = azureLoginUri;
        return;
      }

      const orgResult = await handleGetCurrentOrganization(userData.organization_id);
      const userResult = await handleGetCurrentUser(userData.organization_id);

      if (!orgResult || !userResult) {
        // 組織・ユーザー情報の取得失敗時はログイン画面へ
        dispatch(clearAuth());
        window.location.href = azureLoginUri;
        return;
      }

      navigate(`/${userData.organization_id}/home`);
    };

    // エラー時のリダイレクト処理
    const handleError = () => {
      toast.error(TOAST_MESSAGES.AUTH.SIGNIN_FAILED);
      dispatch(clearAuth());
      window.location.href = azureLoginUri;
    };

    const handleAuthentication = async () => {
      try {
        hasAttemptedAuth.current = true;
        setIsLoading(true);

        const result = await dispatch(fetchUserInfo());

        if (fetchUserInfo.rejected.match(result)) {
          handleError();
          return;
        }

        if (!fetchUserInfo.fulfilled.match(result)) {
          handleError();
          return;
        }

        const response = result.payload as SigninResponse;

        switch (response.type) {
          case "needs_signup":
            dispatch(setSignupAllowed(true));
            navigate("/signup");
            break;

          case "authenticated":
            await handleAuthenticatedUser(response.userInfo);
            break;

          default:
            handleError();
        }
      } catch (error) {
        console.error("致命的なエラー:", error);
        handleError();
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    navigate,
    fetchOrganizationsData,
    handleGetCurrentOrganization,
    handleGetCurrentUser,
    location.search,
  ]);

  return (
    <>
      {showErrorView && <AuthErrorView message={errorMessage} onRetry={handleRetryLogin} />}
      {isLoading && (
        <div className={styles["loadingContainer"]}>
          <div className={styles["spinner"]}></div>
          <p>サインイン中...</p>
        </div>
      )}

      <OrganizationSelectionModal
        isOpen={showOrgModal}
        organizations={organizations}
        onClose={() => setShowOrgModal(false)}
        allowClose={false}
      />
    </>
  );
};

export default Signin;
